/**
 * Comprime uma imagem mantendo qualidade visual.
 * - Redimensiona para no máximo 2048x2048 (preserva aspect ratio).
 * - Converte para JPEG com qualidade 0.85 (ou mantém PNG se houver transparência).
 * - Se o arquivo já for pequeno o suficiente e estiver dentro das dimensões, retorna original.
 * - PDFs/GIFs e tipos não suportados passam sem alteração.
 *
 * Usa Canvas API nativo (sem dependências externas).
 */

const COMPRESSIBLE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

function loadImage(file) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        const url = URL.createObjectURL(file);
        img.onload = () => resolve({ img, url });
        img.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error('Não foi possível carregar a imagem'));
        };
        img.src = url;
    });
}

export async function compressImage(file, options = {}) {
    const {
        maxWidth = 2048,
        maxHeight = 2048,
        quality = 0.85,
        skipIfSmallerThanMB = 1.5,
    } = options;

    if (!COMPRESSIBLE_TYPES.includes(file.type)) {
        return file;
    }

    let loaded;
    try {
        loaded = await loadImage(file);
    } catch (e) {
        return file;
    }

    const { img, url } = loaded;

    try {
        const origWidth = img.naturalWidth;
        const origHeight = img.naturalHeight;

        const precisaRedimensionar = origWidth > maxWidth || origHeight > maxHeight;
        const jaPequeno = file.size <= skipIfSmallerThanMB * 1024 * 1024;

        if (!precisaRedimensionar && jaPequeno) {
            return file;
        }

        let width = origWidth;
        let height = origHeight;
        if (precisaRedimensionar) {
            const ratio = Math.min(maxWidth / width, maxHeight / height);
            width = Math.round(width * ratio);
            height = Math.round(height * ratio);
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, width, height);

        const outputType = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
        const outputQuality = outputType === 'image/png' ? undefined : quality;

        const blob = await new Promise((resolve, reject) => {
            canvas.toBlob(
                b => (b ? resolve(b) : reject(new Error('Falha ao comprimir'))),
                outputType,
                outputQuality
            );
        });

        if (blob.size >= file.size) {
            return file;
        }

        const extension = outputType === 'image/png' ? 'png' : 'jpg';
        const newName = file.name.replace(/\.[^.]+$/, '') + '.' + extension;

        return new File([blob], newName, { type: outputType, lastModified: Date.now() });
    } finally {
        URL.revokeObjectURL(url);
    }
}

export async function compressImages(files, options = {}) {
    return Promise.all(files.map(async (file) => {
        try {
            return await compressImage(file, options);
        } catch (e) {
            return file;
        }
    }));
}
